import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  Plus, 
  Users,
  Calendar,
  Trash2,
  Mail,
  Phone
} from 'lucide-react';
import { getBookings, deleteBooking } from '@/lib/api';
import { format, parseISO } from 'date-fns';
import { toast } from 'sonner';
import BookingDialog from '@/components/dialogs/BookingDialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

export const BookingsView = ({ selectedProperty, properties, onRefresh }) => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [bookingToDelete, setBookingToDelete] = useState(null);

  const fetchBookings = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getBookings(selectedProperty?.id);
      setBookings(response.data);
    } catch (error) {
      console.error('Error fetching bookings:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedProperty]);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  const handleDelete = async () => {
    if (!bookingToDelete) return;
    
    try {
      await deleteBooking(bookingToDelete.id);
      toast.success('Booking deleted successfully');
      fetchBookings();
      onRefresh();
    } catch (error) {
      toast.error('Failed to delete booking');
    } finally {
      setDeleteDialogOpen(false);
      setBookingToDelete(null);
    }
  };

  const getPropertyName = (propertyId) => {
    const prop = properties.find(p => p.id === propertyId);
    return prop?.name || 'Unknown Property';
  };

  return (
    <div className="space-y-6" data-testid="bookings-view">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-manrope font-bold text-3xl text-slate-900 tracking-tight">
            Bookings
          </h1>
          <p className="text-slate-500 mt-1">
            {selectedProperty ? selectedProperty.name : 'All Properties'} - Manage guest reservations
          </p>
        </div>
        <Button 
          onClick={() => setDialogOpen(true)}
          className="bg-slate-900 hover:bg-slate-800"
          data-testid="add-booking-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Booking
        </Button>
      </div>

      {/* Bookings List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="spinner"></div>
        </div>
      ) : bookings.length === 0 ? (
        <Card className="bg-white border border-slate-100">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <Users className="h-16 w-16 text-slate-300 mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2">No bookings yet</h3>
            <p className="text-slate-500 mb-4">Create your first booking to get started</p>
            <Button 
              onClick={() => setDialogOpen(true)}
              data-testid="add-first-booking-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Booking
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {bookings.map((booking) => (
            <Card 
              key={booking.id}
              className="bg-white border border-slate-100 card-hover"
              data-testid={`booking-card-${booking.id}`}
            >
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                        <Users className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900 font-manrope">
                          {booking.guest_name || 'Guest'}
                        </h3>
                        <p className="text-sm text-slate-500">
                          {getPropertyName(booking.property_id)}
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                      <div>
                        <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Check-in</p>
                        <p className="text-sm font-medium text-slate-900 flex items-center gap-1">
                          <Calendar className="h-4 w-4 text-emerald-500" />
                          {format(parseISO(booking.check_in), 'MMM d, yyyy')}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Check-out</p>
                        <p className="text-sm font-medium text-slate-900 flex items-center gap-1">
                          <Calendar className="h-4 w-4 text-red-500" />
                          {format(parseISO(booking.check_out), 'MMM d, yyyy')}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Status</p>
                        <div className="flex items-center gap-2">
                          <span className="badge-booking px-2 py-1 rounded text-xs font-medium">
                            {booking.status}
                          </span>
                          {booking.source_platform && booking.source_platform !== 'internal' && (
                            <span className={`px-2 py-0.5 rounded text-xs font-medium text-white ${
                              booking.source_platform === 'airbnb' ? 'bg-red-500' :
                              booking.source_platform === 'vrbo' ? 'bg-blue-600' :
                              booking.source_platform === 'evolve' ? 'bg-emerald-500' :
                              booking.source_platform === 'booking.com' ? 'bg-blue-800' :
                              booking.source_platform === 'google' ? 'bg-yellow-500' :
                              booking.source_platform === 'microsoft' ? 'bg-sky-500' :
                              'bg-slate-500'
                            }`}>
                              {booking.source_platform.toUpperCase()}
                            </span>
                          )}
                        </div>
                      </div>
                      {booking.total_price > 0 && (
                        <div>
                          <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Total</p>
                          <p className="text-sm font-medium text-slate-900">
                            ${booking.total_price.toLocaleString()}
                          </p>
                        </div>
                      )}
                    </div>

                    {booking.notes && (
                      <p className="text-sm text-slate-500 mt-4 bg-slate-50 p-3 rounded">
                        {booking.notes}
                      </p>
                    )}
                  </div>

                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => {
                      setBookingToDelete(booking);
                      setDeleteDialogOpen(true);
                    }}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    data-testid={`delete-booking-${booking.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Dialogs */}
      <BookingDialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          fetchBookings();
          onRefresh();
        }}
        properties={properties}
        selectedProperty={selectedProperty}
      />

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Booking</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this booking for "{bookingToDelete?.guest_name}"? The associated cleaning task will also be deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-booking-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-booking-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default BookingsView;
